import { i18n } from '@/plugins/i18n'
import { COOKIE_LOGIN_KEY } from '@/constant/cache-key'
import { loadCookie, removeCookie, saveCookie } from '@/utils/cache'

const AUTH_FALLBACK_PATH = '/403'
const DEFAULT_POST_LOGIN_PATH = '/home'

export type AuthFailureReason = 'auth-failed' | 'no-permission'

function isInvalidRedirectValue(url: string) {
  const normalizedUrl = url.trim().toLowerCase()
  return normalizedUrl === '' || normalizedUrl === 'undefined' || normalizedUrl === 'null'
}

function isAllowedAuthPath(pathname: string) {
  const normalizedPathname = pathname.toLowerCase()
  return normalizedPathname.includes('/cas')
    || normalizedPathname.includes('login')
    || normalizedPathname.includes('logout')
}

function getRuntimeOrigin() {
  return window.location.origin
}

function normalizePathname(pathname: string) {
  return pathname.toLowerCase()
}

function isForbiddenFallbackPath(pathname: string) {
  return normalizePathname(pathname) === AUTH_FALLBACK_PATH
}

function normalizeToAppPath(url: string, currentOrigin: string) {
  if (isInvalidRedirectValue(url)) {
    return null
  }

  try {
    const parsedUrl = url.startsWith('/') ? new URL(url, currentOrigin) : new URL(url)
    if (parsedUrl.origin !== currentOrigin) {
      return null
    }

    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`
  }
  catch {
    return null
  }
}

export function isLocalDebugDomain() {
  const { hostname } = window.location
  return hostname === 'localhost'
    || hostname === '127.0.0.1'
    || hostname.startsWith('192.168.')
}

export function saveLoginCallbackUrl(url = window.location.href) {
  saveCookie(COOKIE_LOGIN_KEY, url, { path: '/' })
}

export function clearLoginCallbackUrl() {
  removeCookie(COOKIE_LOGIN_KEY, { path: '/' })
}

export function loadLoginCallbackPath() {
  return resolveLoginCallbackPath(loadCookie(COOKIE_LOGIN_KEY, ''))
}

export function resolveLoginCallbackPath(url: unknown, currentOrigin = getRuntimeOrigin()) {
  if (typeof url !== 'string') {
    return null
  }

  const appPath = normalizeToAppPath(url.trim(), currentOrigin)
  if (!appPath) {
    return null
  }

  const pathname = appPath.split(/[?#]/)[0]
  if (isAllowedAuthPath(pathname) || isForbiddenFallbackPath(pathname)) {
    return null
  }

  return appPath
}

export function resolveLoginCallbackNavigation(currentPath: string, callbackPath: string | null) {
  const normalizedCurrentPath = normalizeToAppPath(currentPath, getRuntimeOrigin())
  if (!normalizedCurrentPath || !callbackPath) {
    return {
      redirectTarget: null,
      shouldClear: false,
    }
  }

  if (callbackPath === normalizedCurrentPath) {
    return {
      redirectTarget: null,
      shouldClear: true,
    }
  }

  const currentPathname = normalizedCurrentPath.split(/[?#]/)[0]
  if (currentPathname !== '/' && currentPathname !== DEFAULT_POST_LOGIN_PATH) {
    return {
      redirectTarget: null,
      shouldClear: false,
    }
  }

  return {
    redirectTarget: callbackPath,
    shouldClear: true,
  }
}

export function appendLocaleToUrl(url: string) {
  const locale = encodeURIComponent(i18n.global.locale.value || 'en-US')
  if (url.includes('lang=')) {
    return url
  }
  return `${url}${url.includes('?') ? '&' : '?'}lang=${locale}`
}

export function resolveAuthRedirectUrl(url: unknown) {
  if (typeof url !== 'string' || isInvalidRedirectValue(url)) {
    return null
  }

  const trimmedUrl = url.trim()

  try {
    if (trimmedUrl.startsWith('/')) {
      if (!isAllowedAuthPath(trimmedUrl.split('?')[0])) {
        return null
      }
      return appendLocaleToUrl(trimmedUrl)
    }

    const parsedUrl = new URL(trimmedUrl)
    if (!['http:', 'https:'].includes(parsedUrl.protocol) || !isAllowedAuthPath(parsedUrl.pathname)) {
      return null
    }

    return appendLocaleToUrl(parsedUrl.toString())
  }
  catch {
    return null
  }
}

export function getLoginEntryUrl() {
  return '/api/casApi/go-cas/anon/login/tgc'
}

export function getAuthFailureFallbackUrl(reason: AuthFailureReason = 'auth-failed') {
  return `${AUTH_FALLBACK_PATH}?reason=${encodeURIComponent(reason)}`
}
